// ProcessDocumentation.js — Process (refonte cartographie / lot 17).
// Deux niveaux : (1) CARTE de la Testing Factory (macro-processus + sous-processus) à l'arrivée,
// (2) DÉTAIL d'un processus via <ProcessModeler/> (IHM/modélisation inchangées) au clic.
// Les rôles (couloirs) sont résolus depuis le référentiel central src/data/cartography/roles.json
// AVANT le rendu, ce qui homogénéise libellés/couleurs sans toucher au moteur de modélisation.
// Mécanisme JSON conservé : require.context sur src/data/processes/ + carte data-driven.
import React, { useMemo, useState } from 'react';
import { GitBranch, MousePointerClick, MoveDiagonal, Workflow, Layers, ArrowLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent, Badge, Icon, EmptyState } from '../components/ui';
import { moduleOf, CRITICALITY } from '../theme/tokens';
import ProcessModeler from '../components/process/ProcessModeler';
import CartographyMap from '../components/process/CartographyMap';
import cartography from '../data/cartography/map.json';
import rolesData from '../data/cartography/roles.json';

const processContext = require.context('../data/processes', false, /\.json$/);
const ROLES = rolesData.roles || {};

// Résout les couloirs (libellé/couleur depuis le référentiel) et propage le rôle aux nœuds.
const resolveProcess = (data) => {
  const lanes = (data.lanes || []).map((l) => {
    const r = ROLES[l.id] || {};
    return { id: l.id, label: l.label || r.label || l.id, color: l.color || r.color || '#64748B' };
  });
  const laneLabel = {};
  lanes.forEach((l) => { laneLabel[l.id] = l.label; });
  const nodes = (data.nodes || []).map((n) => ({ ...n, role: laneLabel[n.lane] || n.role }));
  return { ...data, lanes, nodes };
};

const ProcessDocumentation = () => {
  // Index des processus disponibles (fichier -> données), uniquement les schémas valides.
  const byFile = useMemo(() => {
    const map = {};
    processContext.keys().forEach((k) => {
      try {
        const data = processContext(k);
        if (data && Array.isArray(data.nodes)) map[k.replace('./', '')] = data;
      } catch (e) { /* fichier ignoré */ }
    });
    return map;
  }, []);

  const [selected, setSelected] = useState(null); // null => carte ; sinon nom de fichier

  // Retrouve le macro-processus contenant un fichier (fil d'Ariane).
  const macroOf = (file) => {
    const found = (cartography.macros || []).find((m) => m.subprocesses.some((s) => s.process === file));
    if (found) return found;
    if (cartography.transverse?.subprocesses?.some((s) => s.process === file)) return cartography.transverse;
    return null;
  };

  // --- Vue CARTE -------------------------------------------------------------
  if (!selected || !byFile[selected]) {
    return (
      <CartographyMap
        cartography={cartography}
        byFile={byFile}
        roles={ROLES}
        onOpen={(file) => setSelected(file)}
      />
    );
  }

  // --- Vue DÉTAIL ------------------------------------------------------------
  const raw = byFile[selected];
  const current = resolveProcess(raw);
  const meta = raw.metadata || {};
  const macro = macroOf(selected);
  const mod = meta.module ? moduleOf(meta.module) : null;
  const crit = meta.criticality ? CRITICALITY[meta.criticality] : null;
  const nb = { nodes: current.nodes.length, edges: (current.edges || []).length, lanes: (current.lanes || []).length };

  return (
    <div className="space-y-5">
      {/* Fil d'Ariane + retour */}
      <div className="flex items-center gap-2 text-sm">
        <button
          onClick={() => setSelected(null)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 font-medium"
        >
          <ArrowLeft className="h-4 w-4" />Cartographie
        </button>
        {macro && (
          <>
            <ChevronRight className="h-4 w-4 text-gray-300 dark:text-gray-600" />
            <span className="inline-flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
              <Icon name={macro.icon} className="h-4 w-4" style={{ color: macro.color }} />
              {macro.title}
            </span>
          </>
        )}
      </div>

      {/* En-tête du processus */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <GitBranch className="h-6 w-6 text-blue-500" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{meta.title || 'Processus'}</h1>
          {mod && <Badge color={mod.color}><Icon name={mod.icon} className="h-3 w-3" />{mod.label}</Badge>}
          {crit && <Badge color={crit.color}>{meta.criticality}</Badge>}
        </div>
        {meta.description && <p className="mt-2 text-gray-600 dark:text-gray-300 max-w-3xl">{meta.description}</p>}
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
          {meta.owner && <span className="flex items-center gap-1"><Layers className="h-4 w-4" />{meta.owner}</span>}
          <span>{nb.nodes} étapes</span><span>•</span><span>{nb.edges} transitions</span><span>•</span><span>{nb.lanes} acteurs</span>
          {meta.version && <><span>•</span><span>v{meta.version}</span></>}
        </div>
      </div>

      {/* Aide */}
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
        <span className="flex items-center gap-1.5"><MousePointerClick className="h-3.5 w-3.5" />Cliquez un nœud pour le détail et éclairer son chemin</span>
        <span className="flex items-center gap-1.5"><MoveDiagonal className="h-3.5 w-3.5" />Molette = zoom · glisser = déplacer</span>
      </div>

      {/* Modeleur (inchangé) */}
      <ProcessModeler key={selected} process={current} />

      {/* Synthèse des étapes */}
      <Card>
        <CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Étapes du processus</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {current.nodes.map((n) => (
              <div key={n.id} className="flex items-start gap-2 p-2 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                <Icon name={n.icon || 'Square'} className="h-4 w-4 mt-0.5 text-gray-400 shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{n.name}</div>
                  {n.description && <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{n.description}</div>}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProcessDocumentation;
